import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { apiService } from '../../services/api'
import { 
  Search, 
  FileText, 
  Users, 
  DollarSign, 
  MapPin, 
  Calendar, 
  MessageSquare,
  TrendingUp,
  Clock,
  Shield,
  Check,
  Eye,
  EyeOff,
  Heart
} from 'lucide-react'
import { RfqResponseModal } from '../../components/enterprise/RfqResponseModal'

interface EnterpriseRfq {
  id: string
  title: string
  description: string
  deadline: string
  budgetRange?: string
  location?: string
  buyerCompany?: string
  buyerLocation?: string
  responseCount?: number
  likesCount?: number
  createdAt: string
  attachmentCount?: number
  isSeen?: boolean
  isLiked?: boolean
  hasResponded?: boolean
  responseStatus?: 'PENDING' | 'ACCEPTED' | 'REJECTED'
  responseDate?: string
}

// Response Count Badge Component
const ResponseCountBadge: React.FC<{ count: number }> = ({ count }) => {
  const isHot = count > 10
  const isFirst = count === 0
  
  if (isFirst) {
    return (
      <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
        <MessageSquare className="h-3 w-3 mr-1" />
        Be the first to respond!
      </div>
    )
  }
  
  if (isHot) {
    return (
      <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
        <TrendingUp className="h-3 w-3 mr-1" />
        {count} vendors responded
      </div>
    )
  }
  
  return (
    <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
      <Users className="h-3 w-3 mr-1" />
      {count} vendor{count === 1 ? '' : 's'} responded
    </div>
  )
}

// Likes Count Badge Component
const LikesCountBadge: React.FC<{ count: number }> = ({ count }) => {
  if (count === 0) {
    return (
      <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200">
        <Heart className="h-3 w-3 mr-1" />
        No likes yet
      </div>
    )
  }
  
  const isPopular = count > 5
  
  return (
    <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
      isPopular 
        ? 'bg-pink-50 text-pink-700 border-pink-200' 
        : 'bg-purple-50 text-purple-700 border-purple-200'
    }`}>
      <Heart className="h-3 w-3 mr-1" />
      {count} like{count === 1 ? '' : 's'}
    </div>
  )
}

// Time Left Component
const TimeLeftBadge: React.FC<{ deadline: string }> = ({ deadline }) => {
  const [timeLeft, setTimeLeft] = useState<string>('')
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date()
      const deadlineDate = new Date(deadline)
      const diff = deadlineDate.getTime() - now.getTime()
      
      if (diff <= 0) {
        setTimeLeft('Expired')
        return
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      
      if (days > 0) {
        setTimeLeft(`${days} day${days === 1 ? '' : 's'} left`)
      } else if (hours > 0) {
        setTimeLeft(`${hours} hour${hours === 1 ? '' : 's'} left`)
      } else {
        setTimeLeft('Less than 1 hour left')
      }
    }
    
    calculateTimeLeft()
    const interval = setInterval(calculateTimeLeft, 60000) // Update every minute
    
    return () => clearInterval(interval)
  }, [deadline])
  
  if (timeLeft === 'Expired') {
    return (
      <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
        <Clock className="h-3 w-3 mr-1" />
        Expired
      </div>
    )
  }
  
  const isUrgent = timeLeft.includes('hour') || timeLeft.includes('1 day')
  
  return (
    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
      isUrgent 
        ? 'bg-orange-50 text-orange-700 border-orange-200' 
        : 'bg-gray-50 text-gray-700 border-gray-200'
    }`}>
      <Clock className="h-3 w-3 mr-1" />
      {timeLeft}
    </div>
  )
}

export const VendorRfqPage: React.FC = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'enterprise' | 'government'>('enterprise')
  const [enterpriseSubTab, setEnterpriseSubTab] = useState<'all' | 'seen'>('all')
  const [selectedRfq, setSelectedRfq] = useState<EnterpriseRfq | null>(null)
  const [showResponseModal, setShowResponseModal] = useState(false)
  const [rfqs, setRfqs] = useState<EnterpriseRfq[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [isGovCertified, setIsGovCertified] = useState<boolean | null>(null)

  useEffect(() => {
    if (!user) {
      navigate('/auth/login')
      return
    }

    // Allow VENDOR users with isEnterprise=true or ENTERPRISE_VENDOR role
    const isEnterpriseVendor = (user.role === 'VENDOR' && user.isEnterprise) || user.role === 'ENTERPRISE_VENDOR'
    if (!isEnterpriseVendor) {
      navigate('/', { replace: true })
      return
    }

    // Check if user is verified and email is verified
    if (!user.verified || !user.isEmailVerified) {
      navigate('/auth/verification-required', { 
        state: { 
          message: 'Your enterprise account must be verified before accessing this feature. Please check your email for the verification link.' 
        },
        replace: true 
      })
      return
    }
  }, [user, navigate])

  useEffect(() => {
    if (activeTab === 'enterprise' && user) {
      fetchEnterpriseRfqs()
    } else if (activeTab === 'government' && user) {
      // Check government certification status directly from API
      checkGovCertificationStatus()
    }
  }, [activeTab, user])

  const fetchEnterpriseRfqs = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiService.get<any>('/api/enterprise/rfqs/open') as any
      if (response?.success) {
        setRfqs(response.data?.rfqs || [])
      } else {
        setError('Failed to fetch RFQs')
      }
    } catch (err) {
      console.error('Error fetching RFQs:', err)
      setError('Failed to fetch RFQs')
    } finally {
      setLoading(false)
    }
  }

  const checkGovCertificationStatus = async () => {
    console.log('🔍 Checking government certification status...')
    try {
      setLoading(true)
      setError(null)
      
      // Check government certification status directly from API
      const response = await apiService.get<any>(`/api/gov/check-user-status/${user?.email}`) as any
      console.log('Government certification check response:', response)
      
      if (response?.success && response.data?.isGovCertified) {
        console.log('✅ User is government certified, fetching RFQs...')
        setIsGovCertified(true)
        await fetchGovernmentRfqs()
      } else {
        console.log('❌ User is not government certified')
        setIsGovCertified(false)
        setRfqs([])
        setLoading(false)
      }
    } catch (err) {
      console.error('Error checking government certification:', err)
      // Fallback to checking user object
      if (user?.isGovCertified) {
        setIsGovCertified(true)
        await fetchGovernmentRfqs()
      } else {
        setIsGovCertified(false)
        setRfqs([])
        setLoading(false)
      }
    }
  }

  const fetchGovernmentRfqs = async () => {
    console.log('🔍 fetchGovernmentRfqs called')
    console.log('User object:', user)
    console.log('isGovCertified:', user?.isGovCertified)
    console.log('role:', user?.role)
    
    try {
      setLoading(true)
      setError(null)
      const response = await apiService.get<any>('/api/gov/rfqs/public') as any
      console.log('Government RFQs response:', response)
      if (response?.success) {
        setRfqs(response.data?.rfqs || [])
        console.log('✅ Government RFQs fetched successfully:', response.data?.rfqs?.length || 0, 'RFQs')
      } else {
        setError('Failed to fetch government RFQs')
        console.log('❌ Failed to fetch government RFQs:', response)
      }
    } catch (err) {
      console.error('Error fetching government RFQs:', err)
      setError('Failed to fetch government RFQs')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const filteredRfqs = rfqs.filter(rfq => {
    // First filter by search term
    const matchesSearch = rfq.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rfq.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rfq.buyerCompany?.toLowerCase().includes(searchTerm.toLowerCase())
    
    // Then filter by sub-tab
    if (activeTab === 'enterprise' && enterpriseSubTab === 'seen') {
      return matchesSearch && rfq.isSeen === true
    }
    
    return matchesSearch
  })

  const handleRespondToRfq = (rfq: EnterpriseRfq) => {
    setSelectedRfq(rfq)
    setShowResponseModal(true)
  }

  const handleMarkAsSeen = async (rfqId: string, isSeen: boolean) => {
    try {
      await apiService.patch(`/api/enterprise/rfqs/${rfqId}/mark-seen`, {
        isSeen
      })
      // Update local state on successful request
      setRfqs(prev => prev.map(rfq => 
        rfq.id === rfqId ? { ...rfq, isSeen } : rfq
      ))
    } catch (error) {
      console.error('Error marking RFQ as seen:', error)
    }
  }

  const handleToggleLike = async (rfqId: string, isLiked: boolean) => {
    try {
      await apiService.patch(`/api/enterprise/rfqs/${rfqId}/toggle-like`, {
        isLiked
      })
      // Update local state on successful request
      setRfqs(prev => prev.map(rfq => 
        rfq.id === rfqId ? { ...rfq, isLiked } : rfq
      ))
    } catch (error) {
      console.error('Error toggling RFQ like:', error)
    }
  }

  if (!user || ((user.role !== 'VENDOR' || !user.isEnterprise) && user.role !== 'ENTERPRISE_VENDOR')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking access permissions...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow-sm">
        {/* Header */}
        <div className="border-b border-gray-200">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Browse RFQs</h1>
                <p className="mt-1 text-sm text-gray-500">
                  {((user.role === 'VENDOR' && user.isEnterprise) || user.role === 'ENTERPRISE_VENDOR') 
                    ? 'Find and respond to RFQs from Enterprise Buyers and Government Agencies'
                    : 'Find and respond to RFQs from Enterprise Buyers'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="px-6">
            <div className="flex space-x-8">
              <button
                onClick={() => setActiveTab('enterprise')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'enterprise'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Enterprise RFQs
              </button>
              {/* Only show Government RFQs tab for Enterprise Vendors */}
              {((user.role === 'VENDOR' && user.isEnterprise) || user.role === 'ENTERPRISE_VENDOR') && (
                <button
                  onClick={() => setActiveTab('government')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'government'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Government RFQs
                  {!user.isGovCertified && (
                    <span className="ml-2 px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                      Gov Certified Only
                    </span>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search RFQs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'enterprise' ? (
            <>
              {/* Enterprise Sub-tabs */}
              <div className="mb-6">
                <div className="flex space-x-4 border-b border-gray-200">
                  <button
                    onClick={() => setEnterpriseSubTab('all')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      enterpriseSubTab === 'all'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    ALL RFQs
                  </button>
                  <button
                    onClick={() => setEnterpriseSubTab('seen')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      enterpriseSubTab === 'seen'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    SEEN RFQs
                  </button>
                </div>
              </div>
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading RFQs...</p>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <div className="text-red-600 mb-4">{error}</div>
                  <button
                    onClick={fetchEnterpriseRfqs}
                    className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg"
                  >
                    Retry
                  </button>
                </div>
              ) : filteredRfqs.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {searchTerm 
                      ? 'No RFQs Found' 
                      : enterpriseSubTab === 'seen' 
                        ? 'No Seen RFQs' 
                        : 'No RFQs Available'
                    }
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {searchTerm 
                      ? 'Try adjusting your search terms.'
                      : enterpriseSubTab === 'seen'
                        ? 'You haven\'t marked any RFQs as seen yet.'
                        : 'There are currently no open RFQs from Enterprise Buyers.'
                    }
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-900">
                      {enterpriseSubTab === 'seen' ? 'Seen RFQs' : 'Enterprise RFQs'} ({filteredRfqs.length})
                    </h2>
                  </div>
                  
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {filteredRfqs.map((rfq) => (
                      <div key={rfq.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                            {rfq.title}
                          </h3>
                          <div className="flex flex-col items-end space-y-2">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                              Open
                            </span>
                            <TimeLeftBadge deadline={rfq.deadline} />
                          </div>
                        </div>
                        
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                          {rfq.description}
                        </p>
                        
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center text-sm text-gray-500">
                            <Users className="h-4 w-4 mr-2" />
                            <span>{rfq.buyerCompany || 'Unknown Company'}</span>
                          </div>
                          {rfq.budgetRange && (
                            <div className="flex items-center text-sm text-gray-500">
                              <DollarSign className="h-4 w-4 mr-2" />
                              <span>{rfq.budgetRange}</span>
                            </div>
                          )}
                          {rfq.location && (
                            <div className="flex items-center text-sm text-gray-500">
                              <MapPin className="h-4 w-4 mr-2" />
                              <span>{rfq.location}</span>
                            </div>
                          )}
                          <div className="flex items-center text-sm text-gray-500">
                            <Calendar className="h-4 w-4 mr-2" />
                            <span>Deadline: {formatDate(rfq.deadline)}</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                          <div className="flex flex-col space-y-2">
                            <span className="text-xs text-gray-400">
                              Posted {formatDate(rfq.createdAt)}
                            </span>
                            <div className="flex flex-col space-y-1">
                              <ResponseCountBadge count={rfq.responseCount || 0} />
                              <LikesCountBadge count={rfq.likesCount || 0} />
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {/* Seen/Unseen Toggle */}
                            <button
                              onClick={() => handleMarkAsSeen(rfq.id, !rfq.isSeen)}
                              className={`p-2 rounded-full transition-colors ${
                                rfq.isSeen 
                                  ? 'text-blue-600 hover:text-blue-700' 
                                  : 'text-gray-400 hover:text-gray-600'
                              }`}
                              title={rfq.isSeen ? 'Mark as unseen' : 'Mark as seen'}
                            >
                              {rfq.isSeen ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                            </button>
                            
                            {/* Like/Unlike Toggle */}
                            <button
                              onClick={() => handleToggleLike(rfq.id, !rfq.isLiked)}
                              className={`p-2 rounded-full transition-colors ${
                                rfq.isLiked 
                                  ? 'text-red-600 hover:text-red-700' 
                                  : 'text-gray-400 hover:text-gray-600'
                              }`}
                              title={rfq.isLiked ? 'Unlike' : 'Like'}
                            >
                              {rfq.isLiked ? <Heart className="h-4 w-4 fill-current" /> : <Heart className="h-4 w-4" />}
                            </button>
                            
                            {/* Respond Button */}
                            <button
                              onClick={() => handleRespondToRfq(rfq)}
                              className="flex items-center text-sm text-primary-600 hover:text-primary-700"
                            >
                              <MessageSquare className="h-4 w-4 mr-1" />
                              Respond
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {isGovCertified === false ? (
                <div className="text-center py-12">
                  <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Government RFQ System
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Government RFQs are only available to government-certified vendors.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-md mx-auto">
                    <div className="flex items-center justify-center mb-4">
                      <Shield className="h-8 w-8 text-blue-600 mr-3" />
                      <h4 className="text-lg font-semibold text-blue-900">
                        Get Government Certified
                      </h4>
                    </div>
                    <p className="text-blue-800 text-sm mb-4">
                      Apply for government certification to access government RFQs and expand your business opportunities.
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-center text-sm text-blue-700">
                        <Check className="h-4 w-4 mr-2" />
                        Access to government contracts
                      </div>
                      <div className="flex items-center text-sm text-blue-700">
                        <Check className="h-4 w-4 mr-2" />
                        Higher-value opportunities
                      </div>
                      <div className="flex items-center text-sm text-blue-700">
                        <Check className="h-4 w-4 mr-2" />
                        Verified vendor status
                      </div>
                    </div>
                    <div className="mt-6 space-y-3">
                      <Link
                        to="/company/profile/manage?tab=government"
                        className="inline-flex items-center justify-center w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Shield className="h-4 w-4 mr-2" />
                        Apply for Government Certification
                      </Link>
                      <p className="text-xs text-blue-600">
                        Complete your profile and submit required documentation
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Government RFQs for certified vendors */}
                  {loading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading Government RFQs...</p>
                    </div>
                  ) : error ? (
                    <div className="text-center py-12">
                      <div className="text-red-600 mb-4">{error}</div>
                      <button
                        onClick={fetchGovernmentRfqs}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg"
                      >
                        Retry
                      </button>
                    </div>
                  ) : filteredRfqs.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        No Government RFQs Yet
                      </h3>
                      <p className="text-gray-600 mb-6">
                        No government RFQs are currently available for certified vendors to respond to.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                      {filteredRfqs.map((rfq) => (
                        <div
                          key={rfq.id}
                          className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => {
                            setSelectedRfq(rfq)
                            setShowResponseModal(true)
                          }}
                        >
                          <div className="flex justify-between items-start mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">{rfq.title}</h3>
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              Government RFQ
                            </span>
                          </div>
                          
                          <p className="text-gray-600 text-sm mb-4 line-clamp-3">{rfq.description}</p>
                          
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Budget:</span>
                              <span className="font-medium">{rfq.budgetRange || 'Not specified'}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Deadline:</span>
                              <span className="font-medium">{formatDate(rfq.deadline || 'Not specified')}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Location:</span>
                              <span className="font-medium">{rfq.location || 'Not specified'}</span>
                            </div>
                          </div>
                          
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <span className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800">
                              View & Respond
                              <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                              </svg>
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Response Modal */}
      {selectedRfq && (
        <RfqResponseModal
          isOpen={showResponseModal}
          onClose={() => {
            setShowResponseModal(false)
            setSelectedRfq(null)
          }}
          onSuccess={() => {
            setShowResponseModal(false)
            setSelectedRfq(null)
            // Refresh the appropriate list based on active tab
            if (activeTab === 'government') {
              fetchGovernmentRfqs()
            } else {
              fetchEnterpriseRfqs()
            }
          }}
          rfqId={selectedRfq.id}
          rfqTitle={selectedRfq.title}
          buyerCompany={selectedRfq.buyerCompany || 'Unknown Company'}
          rfqType={activeTab === 'government' ? 'government' : 'enterprise'}
        />
      )}
    </div>
  )
} 